import React, {useEffect, useMemo, useState} from "react";
import axios from "config/axiosConfig";

// 튜토리얼 통계 화면
export default function TutorialStatistics() {
    const [rows, setRows] = useState([]); // [{stepNumber, enterCount, completeCount, exitCount}]
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
    const [endDate, setEndDate] = useState("");   // YYYY-MM-DD

    const requestSearch = () => {
        const params = {};
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        setLoading(true);
        axios.get(`/admin/tutorial/step/statistics`, { params })
            .then(res => {
                setRows(res.data || []);
            })
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        requestSearch();
    }, []);

    // 파생 지표 계산
    const stats = useMemo(() => {
        if (!rows || rows.length === 0) {
            return { avgCompleteRate: 0, bestRateStep: 0 };
        }

        // 평균 완료율 (단계별 완료율의 산술 평균)
        const rates = rows.map(r => {
            const enter = r.enterCount || 0;
            const complete = r.completeCount || 0;
            return enter > 0 ? (complete / enter) : 0;
        });
        const avgCompleteRate = rates.length > 0
            ? rates.reduce((s, v) => s + v, 0) / rates.length
            : 0;

        // 완료율이 가장 높은 단계 번호
        let bestRate = -1;
        let bestRateStep = 0;
        rows.forEach(r => {
            const enter = r.enterCount || 0;
            const complete = r.completeCount || 0;
            const rate = enter > 0 ? (complete / enter) : 0;
            if (rate > bestRate || (rate === bestRate && (r.stepNumber || 0) > bestRateStep)) {
                bestRate = rate;
                bestRateStep = r.stepNumber || 0;
            }
        });

        return { avgCompleteRate, bestRateStep };
    }, [rows]);

    const formatPercent = (v) => `${(v * 100).toFixed(1)}%`;
    const formatNumber = (n) => n?.toLocaleString?.() ?? String(n ?? 0);

    return (
        <div className="App">
            <div className="wrapper">
                <div className="content-wrapper" style={{marginLeft: '0px'}}>
                    <div className="content-header">
                        <div className="container-fluid">
                            <div className="row mb-2">
                                <div className="col-sm-12">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item active">회원 > 튜토리얼 진입 통계</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>

                    <section className="content">
                        <div className="container-fluid">
                            {/* 날짜 필터 */}
                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-tools" style={{marginTop: '20px'}}>
                                            <div className="form-group row" style={{float: 'right', marginRight: '10px'}}>
                                                <label className="col-sm-auto col-form-label" style={{marginLeft: '10px'}}>시작일</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    style={{width: '180px'}}
                                                    value={startDate}
                                                    onChange={e => setStartDate(e.target.value)}
                                                    onKeyDown={e => { if (e.key === 'Enter') { requestSearch(); } }}
                                                />
                                                <label className="col-sm-auto col-form-label" style={{marginLeft: '10px'}}>종료일</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    style={{width: '180px'}}
                                                    value={endDate}
                                                    onChange={e => setEndDate(e.target.value)}
                                                    onKeyDown={e => { if (e.key === 'Enter') { requestSearch(); } }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-info float-right"
                                                    style={{width: '80px', marginLeft: '10px', marginRight: '10px'}}
                                                    onClick={requestSearch}
                                                >
                                                    검색
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary float-right"
                                                    style={{width: '80px', marginRight: '20px'}}
                                                    onClick={() => { setStartDate(""); setEndDate(""); requestSearch(); }}
                                                >
                                                    전체
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 상단 카드 2개 */}
                            <div className="row">
                                <div className="col-md-6 col-sm-12">
                                    <div className="card" style={{minHeight: 180}}>
                                        <div className="card-body" style={{textAlign: 'center'}}>
                                            <div style={{color: '#555', fontWeight: 600}}>평균 완료율</div>
                                            <div style={{fontSize: 64, fontWeight: 800, marginTop: 10}}>
                                                {formatPercent(stats.avgCompleteRate)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6 col-sm-12">
                                    <div className="card" style={{minHeight: 180}}>
                                        <div className="card-body" style={{textAlign: 'center'}}>
                                            <div style={{color: '#555', fontWeight: 600}}>평균 이탈 지점</div>
                                            <div style={{fontSize: 64, fontWeight: 800, marginTop: 10}}>
                                                {stats.bestRateStep}단계
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 표 */}
                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-body table-responsive p-0">
                                            <table className="table table-hover text-nowrap" style={{textAlign: 'center'}}>
                                                <thead>
                                                <tr>
                                                    <th>단계</th>
                                                    <th>진입자</th>
                                                    <th>완료자</th>
                                                    <th>완료율</th>
                                                    <th>이탈자(X클릭)</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {loading && (
                                                    <tr><td colSpan={5} style={{padding: 20}}>불러오는 중...</td></tr>
                                                )}
                                                {!loading && rows && rows.length === 0 && (
                                                    <tr><td colSpan={5} style={{padding: 20}}>데이터가 없습니다.</td></tr>
                                                )}
                                                {!loading && rows && rows.map((r) => {
                                                    const rate = (r.enterCount || 0) > 0 ? (r.completeCount || 0) / r.enterCount : 0;
                                                    return (
                                                        <tr key={r.stepNumber}>
                                                            <td>{r.stepNumber}</td>
                                                            <td>{formatNumber(r.enterCount)}</td>
                                                            <td>{formatNumber(r.completeCount)}</td>
                                                            <td>{formatPercent(rate)}</td>
                                                            <td>{formatNumber(r.exitCount)}</td>
                                                        </tr>
                                                    );
                                                })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}